import React, { useState, useEffect } from 'react';
import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { Edit, MapPin, Percent, Receipt, CalendarClock, Phone, Mail, Settings2, Smartphone, ShieldAlert, Banknote } from 'lucide-react';
import { usePrefetchConfig } from 'features/globalConfiguration/hooks/usePrefetchConfig';
import { useGlobalConfig } from 'features/globalConfiguration/hooks/useGlobalConfig';
import { EditGlobalModal } from 'features/globalConfiguration/components/editGlobalConfigModal';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';

const ConfigTile = ({ icon: Icon, label, value, accent = 'text-primary' }) => (
  <div className="flex items-start gap-4 bg-card border border-border rounded-lg p-5 hover:border-primary/30 transition-colors">
    <div className={`w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 ${accent}`}>
      <Icon size={20} />
    </div>
    <div className="min-w-0">
      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
        {label}
      </h4>
      <p className="text-xl font-semibold text-foreground mt-1 truncate">
        {value ?? '-'}
      </p>
    </div>
  </div>
);

const ConfigSection = ({ title, children }) => (
  <div className="space-y-4">
    <h2 className="text-sm font-semibold text-foreground/80 uppercase tracking-wider">
      {title}
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {children}
    </div>
  </div>
);



const GlobalConfiguration = () => {
    const { prefetchGlobalConfig } = usePrefetchConfig();
    const { data: globalConfigData, isLoading, refetch } = useGlobalConfig();

     const { data: profileData } = useUserProfile();
          const user = profileData?.data;
          
          const restrictedRoles = [
            "delivery_manager",
            "travel_manager",
            "operations_manager",
            "category_manager",
            "finance_manager",
          ];
          
          const showPermissionError = restrictedRoles.includes(
            user?.role?.name
          );


    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    useEffect(() => {
        prefetchGlobalConfig();
    }, [prefetchGlobalConfig]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
      <Layout>
        {showPermissionError ? (
          <div className="flex items-center justify-center min-h-[80vh]">
            <p className="text-destructive text-lg font-medium text-center">
              Error loading configuration: You do not have permission to perform
              this action!
            </p>
          </div>
        ) : (
          <div className={`p-6 bg-background min-h-screen ${PAGE_CONTENT_WIDTH}`}>
            <PageHeader
              icon={Settings2}
              title="Global Configuration"
              subtitle="Manage global system settings"
              actions={
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  <Edit size={16} />
                  Edit Configuration
                </button>
              }
            />

            <div className="space-y-8">
              <ConfigSection title="Service & Discovery">
                <ConfigTile
                  icon={MapPin}
                  label="Service Radius"
                  value={
                    globalConfigData?.data?.search_radius_in_meter != null
                      ? `${globalConfigData.data.search_radius_in_meter / 1000} KM`
                      : null
                  }
                />
              </ConfigSection>

              <ConfigSection title="Pricing & Fees">
                <ConfigTile
                  icon={Receipt}
                  label="Platform Fees"
                  value={globalConfigData?.data?.platform_fees}
                />
                <ConfigTile
                  icon={Percent}
                  label="GST Percentage"
                  value={
                    globalConfigData?.data?.gst_percentage != null
                      ? `${globalConfigData.data.gst_percentage}%`
                      : null
                  }
                />
                <ConfigTile
                  icon={Percent}
                  label="Booking Commission"
                  value={
                    globalConfigData?.data?.booking_commission_percentage != null
                      ? `${globalConfigData.data.booking_commission_percentage}%`
                      : null
                  }
                />
                <ConfigTile
                  icon={CalendarClock}
                  label="Commission Waiver Days"
                  value={globalConfigData?.data?.booking_commission_waiver_days}
                />
              </ConfigSection>

              <ConfigSection title="Cash On Delivery (COD) Settings">
                <ConfigTile
                  icon={Banknote}
                  label="COD Status"
                  accent={
                    globalConfigData?.data?.is_cod_enabled !== false
                      ? 'text-green-500'
                      : 'text-destructive'
                  }
                  value={
                    globalConfigData?.data?.is_cod_enabled !== false
                      ? 'Enabled'
                      : 'Disabled'
                  }
                />
                <ConfigTile
                  icon={Banknote}
                  label="Provider COD Max Due Limit"
                  value={
                    globalConfigData?.data?.cod_max_due_limit != null
                      ? `₹${globalConfigData.data.cod_max_due_limit}`
                      : '₹80'
                  }
                />
              </ConfigSection>

              <ConfigSection title="Support Contact">
                <ConfigTile
                  icon={Phone}
                  label="SOS Helpline Number"
                  value={globalConfigData?.data?.support_contact_number}
                />
                <ConfigTile
                  icon={Mail}
                  label="Contact Email"
                  value={globalConfigData?.data?.support_contact_email}
                />
              </ConfigSection>

              <ConfigSection title="App Version Control — Provider App (Qkly Partner)">
                <ConfigTile
                  icon={Smartphone}
                  label="Min Version — iOS"
                  value={globalConfigData?.data?.provider_app_min_supported_version_ios}
                />
                <ConfigTile
                  icon={Smartphone}
                  label="Min Version — Android"
                  value={globalConfigData?.data?.provider_app_min_supported_version_android}
                />
                <ConfigTile
                  icon={Smartphone}
                  label="Latest Version — iOS"
                  value={globalConfigData?.data?.provider_app_latest_version_ios}
                />
                <ConfigTile
                  icon={Smartphone}
                  label="Latest Version — Android"
                  value={globalConfigData?.data?.provider_app_latest_version_android}
                />
                <ConfigTile
                  icon={ShieldAlert}
                  label="Force Update"
                  accent={
                    globalConfigData?.data?.provider_app_force_update_enabled
                      ? 'text-destructive'
                      : 'text-primary'
                  }
                  value={
                    globalConfigData?.data?.provider_app_force_update_enabled
                      ? 'On'
                      : 'Off'
                  }
                />
              </ConfigSection>

              <ConfigSection title="App Version Control — Customer App (Qkly)">
                <ConfigTile
                  icon={Smartphone}
                  label="Min Version — iOS"
                  value={globalConfigData?.data?.customer_app_min_supported_version_ios}
                />
                <ConfigTile
                  icon={Smartphone}
                  label="Min Version — Android"
                  value={globalConfigData?.data?.customer_app_min_supported_version_android}
                />
                <ConfigTile
                  icon={Smartphone}
                  label="Latest Version — iOS"
                  value={globalConfigData?.data?.customer_app_latest_version_ios}
                />
                <ConfigTile
                  icon={Smartphone}
                  label="Latest Version — Android"
                  value={globalConfigData?.data?.customer_app_latest_version_android}
                />
                <ConfigTile
                  icon={ShieldAlert}
                  label="Force Update"
                  accent={
                    globalConfigData?.data?.customer_app_force_update_enabled
                      ? 'text-destructive'
                      : 'text-primary'
                  }
                  value={
                    globalConfigData?.data?.customer_app_force_update_enabled
                      ? 'On'
                      : 'Off'
                  }
                />
              </ConfigSection>
            </div>

            {isEditModalOpen && (
              <EditGlobalModal
                isOpen={isEditModalOpen}
                onClose={() => {
                  setIsEditModalOpen(false);
                  refetch();
                }}
                defaultValues={globalConfigData?.data}
              />
            )}
          </div>
        )}
      </Layout>
    );
}

export default GlobalConfiguration